import { getFilterCatalog } from "../../core/service";

// Project filter catalog: ONE call to /filter_catalog (the dashboard fact views) returns every
// dimension's option values, which we index by `get_list_field` and serve every per-field candidate
// lookup from — in memory. This REPLACES the old per-field /get_list fan-out (20+ permission-scoped
// calls, one per field, each with heavy controller overhead); the resolver/apply layers are unchanged
// because `fetchCandidates` keeps its signature and `{ id, name }[]` return shape.
//
// In-memory cache only (no localStorage), keyed by PROJECT scope — one entry holds the WHOLE catalog
// index. `cache` coalesces the warm + every per-field lookup within a page session onto ONE network call;
// `pending` coalesces concurrent loads (the warm racing the first query, or several fields resolving at
// once) onto ONE in-flight request. We deliberately do NOT persist across reloads: /filter_catalog is a
// single index-bounded (sub-200ms) call, so the saving from a 12h persistent cache wasn't worth serving
// stale options — an edited label / new option / option gloss must show up on the next page load, not 12h
// later (grounding principle). The cost is one cheap fetch per fresh load / hard reload / new tab.
const cache = new Map();
const pending = new Map();

// Read/write the in-memory cache. (Previously read/write-through to a 12h localStorage layer; removed so
// the catalog can never go stale across a reload.)
const cacheGet = key => (cache.has(key) ? cache.get(key) : undefined);
const cacheSet = (key, value) => cache.set(key, value);

// Catalog scope is by project only (multi-project dashboards join their ids). The user's visible-building
// scope (D2) rides along as `buildingIds` when supplied, and is part of the cache key so a scoped catalog
// can't be served to an unscoped request (or vice-versa).
const scopeKeyOf = context => {
    const projectScope =
        context.projectIds && context.projectIds.length
            ? [...context.projectIds].sort().join(",")
            : context.projectId != null
            ? String(context.projectId)
            : "";
    const buildingScope = context.buildingIds && context.buildingIds.length ? [...context.buildingIds].sort().join(",") : "";
    return buildingScope ? `${projectScope}#${buildingScope}` : projectScope;
};

// `get_list_field` is the catalog's join key — the dashboard getListKey joined by "|" (e.g.
// "trades.id|trades.name", "buildings.criticality", "priority_elements.<uuid>"). The /filter_catalog
// rows are keyed the same way, so a field's options are a direct lookup.
const glfKeyOf = field => (Array.isArray(field) ? field.join("|") : "");

// Index the catalog response by get_list_field -> [{ id, name, gloss? }] (the shape the resolver/apply
// expect). `gloss`, when the catalog supplies one (ordinal/quality options), is carried through so the
// resolver's embedding tier sees it — display + apply still use `name`, so the apply path is unchanged.
const buildIndex = catalog => {
    const index = {};
    ((catalog && catalog.fields) || []).forEach(f => {
        index[f.get_list_field] = (f.options || []).map(o => {
            const candidate = { id: o.id !== undefined && o.id !== null ? o.id : null, name: o.name };
            if (o.gloss) candidate.gloss = o.gloss;
            return candidate;
        });
    });
    return index;
};

/**
 * Load (and cache) the whole project catalog as an index { get_list_field -> [{id,name}] }.
 * Concurrent calls for the same scope coalesce onto ONE /filter_catalog request. Exported so the
 * background warm and a lazy first query both go through the same single-flight load.
 */
export const loadCatalog = async (context = {}) => {
    const key = scopeKeyOf(context);
    const cached = cacheGet(key);
    if (cached !== undefined) return cached;
    if (pending.has(key)) return pending.get(key);

    const fetchPromise = (async () => {
        const catalog = await getFilterCatalog({
            projectId: context.projectId,
            projectIds: context.projectIds,
            buildingIds: context.buildingIds
        });
        const index = buildIndex(catalog);
        // Dev-only: expose what the catalog COVERS (regardless of data) + what's present for this project,
        // so the e2e coverage audit can flag DRIFT — a dashboard filter Otto exposes but the catalog
        // doesn't cover (which would silently starve resolution). Never set in production.
        if (process.env.NODE_ENV !== "production" && typeof window !== "undefined") {
            window.__OTTO_CATALOG__ = {
                covered: (catalog && catalog.covered_get_list_fields) || [],
                present: Object.keys(index)
            };
        }
        return index;
    })();
    pending.set(key, fetchPromise);

    let index;
    try {
        index = await fetchPromise;
    } finally {
        pending.delete(key);
    }
    cacheSet(key, index);
    return index;
};

// The onCatalogFetch tell, in one place: every candidate lookup — project catalog OR page-supplied source —
// reports through here, and tracing must never break a fetch.
const reportFetch = (context, entry) => {
    if (typeof context.onCatalogFetch !== "function") return;
    try {
        context.onCatalogFetch(entry);
    } catch (e) {
        /* tracing must never break the fetch */
    }
};

/**
 * Per-field candidate lookup, served from the in-memory project catalog (no per-field network call).
 * Same signature + `{ id, name }[]` return as before, so orchestrator / resolver / apply are unchanged:
 *   - controlled vocabulary -> pass `search=""` and get the field's FULL option list
 *   - high-cardinality dim   -> pass a phrase; we substring-filter the field's options client-side
 *     (a case-insensitive `name` contains, replacing the old server-side LIKE).
 * @param fieldMeta dimension entry: { id, get_list_field, value_kind, searchKey, ... }
 * @param search    value phrase to narrow by (optional)
 * @param context   { projectId, projectIds, buildingIds, onCatalogFetch } — scope + tracing hook
 * @returns Promise<Array<{ id, name }>>
 */
export const fetchCandidates = async (fieldMeta, search, context = {}) => {
    // PAGE-SUPPLIED SOURCE. The project catalog view carries recommendations vocabulary; a page whose fields
    // live elsewhere (Asset Management) stamps `optionSource` on its metas and injects a matching source, so
    // its lookups go to that page's own endpoint instead of returning [] and starving the resolver. Routed on
    // the FIELD rather than the mount, so a page can mix catalog-backed and page-backed fields, and a page
    // that stamps nothing takes the byte-identical path it always did.
    const source = context.candidateSource;
    if (source && fieldMeta && fieldMeta.optionSource && source.key === fieldMeta.optionSource) {
        // TRACED like the catalog path, and traced HERE rather than inside each source (FTO-85). This branch
        // used to return before the onCatalogFetch hook below, so on any page served by its own endpoint the
        // `catalog.fetch` span held only the fields that fell through to the project catalog — i.e. exactly
        // the ones that could never have matched. In trace 24e7f9b4 that made the span show two useless
        // `criticality` lookups and NOT the asset-condition fetch Otto actually resolved against, so the
        // starvation question the span exists to answer couldn't be answered at all. Central, so a new page
        // source is traced without having to remember; `fetchProbe` is the optional seam a source fills in to
        // report a cache hit (it has its own cache — this layer's `cache` is only the project catalog).
        const probe = {};
        try {
            const options = await source.fetch(fieldMeta, search, { ...context, fetchProbe: probe });
            const list = Array.isArray(options) ? options : [];
            reportFetch(context, {
                field: fieldMeta.id,
                endpoint: source.key,
                searchKey: fieldMeta.searchKey || fieldMeta.assetGetListKey || glfKeyOf(fieldMeta.get_list_field),
                search: search || "",
                count: list.length,
                names: list.slice(0, 12).map(o => o.name),
                cached: !!probe.cached
            });
            return list;
        } catch (err) {
            reportFetch(context, {
                field: fieldMeta.id,
                endpoint: source.key,
                status: "error",
                error: String((err && err.message) || err)
            });
            return []; // best-effort, exactly like the catalog path below
        }
    }

    const glf = glfKeyOf(fieldMeta && fieldMeta.get_list_field);
    if (!glf) return [];

    // Whether THIS lookup is served without a new network call (catalog already loaded). The first field
    // of a fresh project pays the single /filter_catalog load; the rest are cache hits. Threaded to the
    // tracing dashboard via onCatalogFetch (endpoint is now the catalog view, not a per-field /get_list).
    const key = scopeKeyOf(context);
    const cachedBefore = cache.has(key);

    let index;
    try {
        index = await loadCatalog(context);
    } catch (err) {
        reportFetch(context, { field: fieldMeta.id, endpoint: "catalogView", status: "error", error: String((err && err.message) || err) });
        return [];
    }

    let options = index[glf] || [];
    if (search) {
        const q = String(search).toLowerCase();
        options = options.filter(o => (o.name || "").toLowerCase().indexOf(q) !== -1);
    }

    reportFetch(context, {
        field: fieldMeta.id,
        endpoint: "catalogView",
        searchKey: fieldMeta.searchKey || glf,
        search: search || "",
        count: options.length,
        names: options.slice(0, 12).map(o => o.name),
        cached: !!cachedBefore
    });
    return options;
};

/**
 * CASCADED option counts: one /filter_catalog call WITH the dashboard's applied filters (D3), returning
 * { get_list_field -> { optionKey -> count } } where optionKey is String(id ?? name). Used AFTER
 * resolution to hide/flag suggestions that would return an EMPTY dashboard under the current filters —
 * resolution itself still grounds against the FULL vocabulary (candidate-starvation guarantee), so this
 * never affects matching, only what Otto offers/warns about. Deliberately UNCACHED: it's only called when
 * filters are active, filter state changes turn-to-turn, and a stale count right after an apply is exactly
 * the bug this exists to prevent.
 */
export const fetchCascadedCounts = async (context = {}, activeFilters = null) => {
    const catalog = await getFilterCatalog({
        projectId: context.projectId,
        projectIds: context.projectIds,
        buildingIds: context.buildingIds,
        activeFilters
    });
    const counts = {};
    ((catalog && catalog.fields) || []).forEach(f => {
        const byOption = counts[f.get_list_field] || {};
        (f.options || []).forEach(o => {
            byOption[String(o.id !== undefined && o.id !== null ? o.id : o.name)] = o.count || 0;
        });
        counts[f.get_list_field] = byOption;
    });
    return counts;
};

/**
 * CASCADED options WITH names + the record TOTAL: same /filter_catalog D3 call as fetchCascadedCounts,
 * returning { total, byGlf, uncounted } where byGlf is { get_list_field -> [{ id, name, count }] } (so
 * callers can SAY which options remain viable — "possible Trade options: …") and `total` is the number of
 * records matching the cascade (the dashboard's post-Apply "N Records", surfaced by Otto's Apply button).
 * `total` is null when the backend doesn't send it (older deploy) so the count simply doesn't show.
 * `uncounted` lists the requested dimensions the cascade could NOT count (FCI/EFCI bands, dynamic fields);
 * when non-empty, `total` + the per-option counts DON'T reflect those filters, so the caller must NOT
 * present them as the dashboard's result. Same uncached, best-effort contract as fetchCascadedCounts.
 */
export const fetchCascadedOptions = async (context = {}, activeFilters = null) => {
    const catalog = await getFilterCatalog({
        projectId: context.projectId,
        projectIds: context.projectIds,
        buildingIds: context.buildingIds,
        activeFilters
    });
    const byGlf = {};
    ((catalog && catalog.fields) || []).forEach(f => {
        byGlf[f.get_list_field] = (f.options || []).map(o => ({
            id: o.id !== undefined && o.id !== null ? o.id : null,
            name: o.name,
            count: o.count || 0
        }));
    });
    const total = catalog && typeof catalog.total_count === "number" ? catalog.total_count : null;
    const uncounted = (catalog && Array.isArray(catalog.uncounted_dimensions) && catalog.uncounted_dimensions) || [];
    return { total, byGlf, uncounted };
};

export const clearCatalogCache = () => {
    cache.clear();
    pending.clear();
};
